import {constant} from '@packages/utilities/fp/utils';
import {
  fieldSlug as formatFieldSlug,
  restoreSlug,
} from '@packages/systems/dynamo/utils/SlugUtils';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {isCmsItemTypeName} from '../utils/isCmsItemTypeName';
import {Some, None, SomeT, NoneT} from './option';

import type {AdaptedData, RawData, RawDataObj} from '../types';

type DynamoEnvProxy = {
  get(target: never, key: string | symbol): SomeT | NoneT | undefined;
  has(target: never, key: string | symbol): boolean;
  getOwnPropertyDescriptor(
    target: never,
    key: string | symbol
  ):
    | {
        value?: SomeT | NoneT;
        configurable: boolean;
        writable: boolean;
        enumerable?: boolean | undefined;
      }
    | undefined;
  ownKeys: <a>(arg1: a) => Array<string | symbol>;
};

type NoTypeNameProxy = {
  get(target: never, key: string | symbol): unknown;
  has(target: never, key: string | symbol): boolean;
  getOwnPropertyDescriptor(
    target: never,
    key: string | symbol
  ): PropertyDescriptor | undefined;
  ownKeys: <a>(arg1: a) => Array<string | symbol>;
};

/**
 * Wraps gql response data in a Proxy to map it to Dynamo env type
 *
 * Maps binding paths to gql field slugs and values of graphl fields
 * to Some/None tagged Option values.
 *
 * For example, given the following GraphQL response `data` for a CMS
 * item this function returns a Proxy that hides GraphQL field slugs
 * and internal properties (like __typename) from consumers.
 *
 * Raw GraphQL response data for a Blog Post CMS item
 * ```
 * {
 *   id: 'post-id',
 *   f_name_: 'A Blog Post',
 *   f_body_: null,
 *   f_author_: {
 *     id: 'author-id',
 *     f_name_: 'An Author',
 *     __typename: 'CMSItem',
 *   },
 *   __typename: 'CMSItem'
 * }
 * ```
 *
 * Proxied shape for the same Blog Post CMS item
 * ```
 * {
 *   id: Some('post-id'),
 *   name: Some('A Blog Post'),
 *   body: None
 *   author: Some({id: 'author-id'}),
 *   'author.id': Some('author-id'),
 *   'author.name': Some('An Author'),
 * }
 * ```
 *
 * Consumers of the proxied data can access properties directly using
 * binding paths found from element data `proxied['author.name']` without
 * needing knowledge of the field slug formatting used by our GraphQL
 * API.
 */
export function createDynamoEnvProxy(
  data: AdaptedData,
  dynContextData: AdaptedData
): RawData {
  /*
    For now, this function only meant for proxying GraphQL response
    data for CMS collection template page items. The if -clause below
    prevents any `data` from being proxied if
    1. it's a raw GraphQL response data object
    4. it's not identifiable as GraphQL response data for CMS page item
  */
  if (data && isRawDataObject(data) && isCmsItemTypeData(data)) {
    // create proxies only for CMS item data
    return memoized_createDynamoEnvProxy(data)(dynContextData);
  }

  return null;
}

// memoize Proxy creation to ensure referential equality breaks only if
// referential equality breaks on the underlying GraphQL response data
const memoized_createDynamoEnvProxy = weakMemo((data: RawDataObj) =>
  singleMemo((dynContextData: AdaptedData): RawData => {
    return new Proxy<DynamoEnvProxy>(
      {} as DynamoEnvProxy,
      dynamoEnvProxyHandlers(data, dynContextData)
    );
  })
);

function dynamoEnvProxyHandlers(
  data: RawDataObj,
  dynContextData: AdaptedData
): DynamoEnvProxy {
  return {
    get: (_target: never, prop: string | symbol) =>
      dynamoEnvProxy_get(data, dynContextData, prop),
    has: (_target: never, prop: string | symbol) => typeof prop === 'string',
    getOwnPropertyDescriptor: (_target: never, prop: string | symbol) =>
      dynamoEnvProxy_getOwnPropertyDescriptor(data, dynContextData, prop),
    ownKeys: constant<string[]>(
      dynamoEnvProxy_ownKeys(data).concat(
        dynContextProxy_ownKeys(dynContextData)
      )
    ),
  };
}

/**
 * Getter retrieves value from `target` that corresponds to `prop`
 *
 * Maps the incoming `prop` (for example 'name') to a gql field slug
 * (for example 'f_name_') and gets the value from the underlying
 * GraphQL response data object `target`.
 *
 * Returns `undefined` if `prop` is not a string.
 */
function dynamoEnvProxy_get(
  target: RawDataObj,
  dynContextTarget: AdaptedData,
  prop: string | symbol
) {
  if (typeof prop === 'string') {
    if (prop.startsWith('DYN_CONTEXT')) {
      return toOptionDynContextValue(
        dynamoEnvDynContextPropertyGetter(prop, dynContextTarget)
      );
    }

    return dynamoEnvPropertyGetter(
      prop,
      target,
      toOptionalDynamoEnvValue,
      None
    );
  }
  return undefined;
}

/**
 * Intercepts getOwnPropertyDescriptor calls
 */
function dynamoEnvProxy_getOwnPropertyDescriptor(
  target: RawDataObj,
  dynContextTarget: AdaptedData,
  prop: string | symbol
) {
  if (typeof prop === 'string') {
    // fallback value describes properties as non-enumerable
    // because those properties are missing from ownKeys return value
    const fallback = {
      value: None,
      writable: false,
      configurable: true,
      enumerable: false,
    };

    if (prop.startsWith('DYN_CONTEXT')) {
      const value = dynamoEnvDynContextPropertyGetter(prop, dynContextTarget);
      if (value === null) {
        return fallback;
      } else {
        return {
          value: toOptionDynContextValue(value),
          writable: false,
          configurable: true,
          enumerable: true,
        };
      }
    }

    return dynamoEnvPropertyGetter(
      prop,
      target,
      toDynamoEnvPropertyDescriptor,
      fallback
    );
  }
  return undefined;
}

/**
 * Intercepts various operations to read own property keys of `target`
 *
 * These operations include
 * 1. Object.getOwnPropertyNames()
 * 2. Object.getOwnPropertySymbols()
 * 3. Object.keys()
 * 4. Reflect.ownKeys()
 */
function dynamoEnvProxy_ownKeys(
  target: RawDataObj,
  recurse: boolean = true
): string[] {
  // we use Object.getOwnPropertyNames because symbol properties in
  // GraphQL response data are not relevant for our use case
  return Object.getOwnPropertyNames(target).reduce<string[]>(
    (ownKeys, propName) => {
      if (propName !== '__typename') {
        const key = propName === 'id' ? propName : restoreSlug(propName);
        ownKeys.push(key);

        const propValue = target[propName];
        if (
          recurse &&
          isRawDataObject(propValue) &&
          isCmsItemTypeData(propValue)
        ) {
          ownKeys.push(
            ...dynamoEnvProxy_ownKeys(propValue, false).map(
              (nestedKey) => `${key}.${nestedKey}`
            )
          );
        }
      }

      return ownKeys;
    },
    ['_this']
  );
}

function dynContextProxy_ownKeys(dynContextData: AdaptedData) {
  if (
    dynContextData &&
    isRawDataObject(dynContextData) &&
    isCmsItemTypeData(dynContextData) &&
    'id' in dynContextData &&
    typeof dynContextData.id === 'string'
  ) {
    return Object.getOwnPropertyNames(dynContextData).reduce<string[]>(
      (ownKeys, gqlFieldSlug) => {
        const fieldValue = dynContextData[gqlFieldSlug];

        if (
          fieldValue &&
          isRawDataObject(fieldValue) &&
          isCmsItemTypeData(fieldValue) &&
          'id' in fieldValue &&
          typeof fieldValue.id === 'string'
        ) {
          ownKeys.push(`DYN_CONTEXT.${restoreSlug(gqlFieldSlug)}`);
        } else if (
          Array.isArray(fieldValue) &&
          fieldValue.every(
            (item) =>
              isRawDataObject(item) &&
              isCmsItemTypeData(item) &&
              'id' in item &&
              typeof item.id === 'string'
          )
        ) {
          ownKeys.push(`DYN_CONTEXT.${restoreSlug(gqlFieldSlug)}`);
        }

        return ownKeys;
      },
      ['DYN_CONTEXT']
    );
  }

  return [];
}

function createCmsItemProxy(data: AdaptedData) {
  if (data && isRawDataObject(data) && isCmsItemTypeData(data)) {
    return memoized_createCmsItemProxy(data);
  }

  return null;
}

const memoized_createCmsItemProxy = weakMemo((data: RawDataObj) => {
  return new Proxy<DynamoEnvProxy>(
    {} as DynamoEnvProxy,
    cmsItemProxyHandlers(data)
  );
});

function cmsItemProxyHandlers(data: RawDataObj): DynamoEnvProxy {
  return {
    get: (_target: never, prop: string) => cmsItemProxy_get(data, prop),
    has: (_target: never, prop: string) => cmsItemProxy_has(data, prop),
    getOwnPropertyDescriptor: (_target: never, prop: string | symbol) =>
      cmsItemProxy_getOwnPropertyDescriptor(data, prop),
    ownKeys: constant<Array<string | symbol>>(cmsItemProxy_ownKeys(data)),
  };
}

function cmsItemProxy_get(target: RawDataObj, prop: string) {
  if (prop === 'id' && prop in target) {
    // TODO: Determine what ItemRef type value looks like in env
    return target[prop];
  }
  return undefined;
}

function cmsItemProxy_has(target: RawDataObj, prop: string) {
  // TODO: Determine what ItemRef type value looks like in env
  return prop === 'id' && prop in target;
}

function cmsItemProxy_getOwnPropertyDescriptor(
  target: RawDataObj,
  prop: string | symbol
) {
  if (prop === 'id' && prop in target) {
    // TODO: Determine what ItemRef type value looks like in env
    return {
      ...Object.getOwnPropertyDescriptor(target, prop),
      configurable: true,
      writable: false,
    };
  }
  return undefined;
}

function cmsItemProxy_ownKeys(target: RawDataObj): Array<string | symbol> {
  // TODO: Determine what ItemRef type value looks like in env
  return Reflect.ownKeys(target).filter((ownKey) => ownKey === 'id');
}

function createNoTypenameProxy(data: AdaptedData) {
  if (data && isRawDataObject(data) && !isCmsItemTypeData(data)) {
    return memoized_createNoTypenameProxy(data);
  }

  return null;
}

const memoized_createNoTypenameProxy = weakMemo((data: RawDataObj) => {
  return new Proxy<NoTypeNameProxy>(
    {} as NoTypeNameProxy,
    noTypenameProxyHandlers(data)
  );
});

function noTypenameProxyHandlers(data: RawDataObj): NoTypeNameProxy {
  return {
    get: (_target: never, prop: string) => noTypenameProxy_get(data, prop),
    has: (_target: never, prop: string) => noTypenameProxy_has(data, prop),
    getOwnPropertyDescriptor: (_target: never, prop: string | symbol) =>
      noTypenameProxy_getOwnPropertyDescriptor(data, prop),
    ownKeys: constant<Array<string | symbol>>(noTypenameProxy_ownKeys(data)),
  };
}

function noTypenameProxy_get(
  target:
    | RawDataObj
    | {
        readonly [key: string]: any;
      },
  prop: string | symbol
): unknown {
  if (prop !== '__typename' && prop in target && typeof prop === 'string') {
    const propValue = target[prop];
    return createNoTypenameProxy(propValue) || propValue;
  }
  return undefined;
}

function noTypenameProxy_has(target: RawDataObj, prop: string) {
  if (prop !== '__typename' && prop in target) {
    return prop in target;
  }
  return false;
}

function noTypenameProxy_getOwnPropertyDescriptor(
  target: RawDataObj,
  prop: string | symbol
) {
  if (prop !== '__typename' && prop in target) {
    return {
      value: noTypenameProxy_get(target, prop),
      configurable: true,
      enumerable: true,
      writable: false,
    };
  }
  return undefined;
}

function noTypenameProxy_ownKeys(target: RawDataObj): Array<string | symbol> {
  return Reflect.ownKeys(target).filter((ownKey) => ownKey !== '__typename');
}

const propsBypassingFormatting = [
  'id',
  'fullSlug',
  'noSearch',
  'ecSkuInventoryQuantity',
];

const getFormattedPropName = (prop: string): string =>
  // Note: some props don't use gql field slug formatting
  propsBypassingFormatting.includes(prop) ? prop : formatFieldSlug(prop);

/*
  Used to identify raw GraphQL response data objects `{...}`
  1. it's null or undefined
  2. it's an array
  3. it's not an object
*/
const isRawDataObject = (data: AdaptedData): boolean =>
  data != null && !Array.isArray(data) && typeof data === 'object';

/*
  Checking `data.__typename` to ensure it represents a CMS item is a
  bit hacky, but should be robust enough for initial implementation.

  Once Dynamo query builder uses Dynamo Gateway as source of truth,
  we will be able to use Data Source Definitions as basis for building
  proxies to bridge binding evaluation and GraphQL response data.
*/
const isCmsItemTypeData = (data: AdaptedData) =>
  data && '__typename' in data && typeof data.__typename === 'string'
    ? isCmsItemTypeName(data.__typename)
    : false;

/**
 * Gets the value from GraphQL data for a dot-separated binding path
 *
 * The mapValue function is used to map found value to return value.
 * The notFoundValue is returned if binding path does not have a
 * corresponding value.
 */
function dynamoEnvPropertyGetter<T>(
  dotSeparatedBindingPath: string,
  gqlData: RawDataObj,
  mapValue: (arg1?: any) => T,
  notFoundValue: T
): T {
  if (dotSeparatedBindingPath === '_this') {
    return mapValue(gqlData);
  }

  const bindingPath = dotSeparatedBindingPath.split('.');
  let target = gqlData;
  let foundValue;

  for (let i = 0; i < bindingPath.length; i++) {
    const slug = bindingPath[i];
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    const gqlSlug = getFormattedPropName(slug);

    if (slug === '__typename' || !(gqlSlug in target)) {
      return notFoundValue;
    }

    const propValue = target[gqlSlug];
    const isLast = i === bindingPath.length - 1;

    if (isLast) {
      foundValue = propValue;
    } else if (isRawDataObject(propValue) && isCmsItemTypeData(propValue)) {
      target = propValue;
    } else {
      return notFoundValue;
    }
  }

  return mapValue(foundValue);
}

function toOptionalDynamoEnvValue(rawValue: any) {
  const value =
    createCmsItemProxy(rawValue) || createNoTypenameProxy(rawValue) || rawValue;

  return value == null ? None : Some(value);
}

function toDynamoEnvPropertyDescriptor(rawValue: any) {
  return {
    value: toOptionalDynamoEnvValue(rawValue),
    configurable: true,
    writable: false,
    enumerable: true,
  };
}

function dynamoEnvDynContextPropertyGetter(
  dynContextBindingPath: string,
  dynContextData: AdaptedData
): string | string[] | null {
  if (
    dynContextData &&
    isRawDataObject(dynContextData) &&
    isCmsItemTypeData(dynContextData) &&
    'id' in dynContextData &&
    typeof dynContextData.id === 'string'
  ) {
    if (dynContextBindingPath === 'DYN_CONTEXT') {
      return dynContextData.id;
    }

    const slug = dynContextBindingPath.replace(/^DYN_CONTEXT\./, '');
    const gqlSlug = getFormattedPropName(slug);
    const fieldValue =
      gqlSlug in dynContextData ? dynContextData[gqlSlug] : null;

    if (
      fieldValue &&
      isRawDataObject(fieldValue) &&
      isCmsItemTypeData(fieldValue) &&
      'id' in fieldValue &&
      typeof fieldValue.id === 'string'
    ) {
      return fieldValue.id;
    }

    if (
      Array.isArray(fieldValue) &&
      fieldValue.every(
        (item) =>
          isRawDataObject(item) &&
          isCmsItemTypeData(item) &&
          'id' in item &&
          typeof item.id === 'string'
      )
    ) {
      return fieldValue.map((item) => item.id);
    }
  }

  return null;
}

function toOptionDynContextValue(value: string | string[] | null) {
  return value === null ? None : Some(value);
}
